import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Phone, Mail, MapPin, CheckCircle2, Zap } from 'lucide-react';
import axios from 'axios';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

// --- HELPER COMPONENTS ---

const ContactInfoItem = ({ icon, title, desc, delay }) => (
  <motion.div 
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ delay }}
    className="flex items-center gap-3 group"
  >
    <div className="bg-orange-500 p-2 rounded-lg text-white shrink-0 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[8px] font-black text-orange-500 uppercase tracking-tighter">{title}</p>
      <p className="text-xs md:text-sm font-bold text-slate-200 truncate group-hover:text-white transition-colors">{desc}</p>
    </div>
  </motion.div>
  
);

const CompactInput = ({ label, delay, ...props }) => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="space-y-1"
  >
    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">{label}</label>
    <input 
      required 
      {...props}
      className="w-full bg-slate-50 border border-slate-200 px-4 py-2.5 md:py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all font-semibold text-slate-800 text-sm md:text-base hover:border-orange-200"
    />
  </motion.div>
);

const SuccessUI = ({ onReset }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.9 }} 
    animate={{ opacity: 1, scale: 1 }} 
    className="text-center py-10"
  >
    <div className="relative inline-block">
        <motion.div 
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.2, 0.5] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="absolute inset-0 bg-green-500 rounded-full blur-xl"
        />
        <CheckCircle2 size={60} className="text-green-500 relative z-10 mx-auto mb-4" />
    </div>
    <h2 className="text-2xl font-black text-slate-900 uppercase italic">Message Received</h2>
    <p className="text-slate-500 text-sm mt-2">Our engineers are reviewing your request.</p>
    <button 
      onClick={onReset} 
      className="mt-8 px-6 py-2 bg-slate-900 text-white rounded-full font-black uppercase text-[10px] tracking-widest hover:bg-orange-500 transition-colors"
    >
      New Message
    </button>
  </motion.div>
);

// --- MAIN COMPONENT ---

const ContactUs = () => {
  useEffect(() => {
  document.title = "Marvel Sato | Contact Us";
}, []);

  const [formData, setFormData] = useState({ customerName: '', customerEmail: '', customerPhone: '', message: '' });
  const [status, setStatus] = useState({ loading: false, success: false, error: null });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/contact/message`, formData);
      if (response.status === 200 || response.status === 201) {
        setStatus({ loading: false, success: true, error: null });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (err) {
      // FIX: Extract only the message string, or a fallback string
      const errorMessage = err.response?.data?.message || "System Connection Error.";
      setStatus({ loading: false, success: false, error: errorMessage });
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen overflow-x-hidden selection:bg-orange-500 selection:text-white">
      <Header />

      {/* --- KINETIC HERO SECTION --- */}
      {/* Fixed: Increased pt-32 (mobile) and pt-48 (laptop) to clear the header */}
      <section className="relative min-h-[45vh] md:min-h-[55vh] flex items-center justify-center overflow-hidden pt-32 md:pt-48 pb-10">
        <motion.div 
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute inset-0 z-0"
        >
          <img 
            src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&q=80" 
            alt="Industrial Engineering" 
            className="w-full h-full object-cover grayscale-[30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-transparent to-transparent opacity-90" />
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4"
        >
          <motion.span 
            initial={{ opacity: 0, letterSpacing: "0.1em" }}
            animate={{ opacity: 1, letterSpacing: "0.4em" }}
            transition={{ delay: 0.5, duration: 1 }}
            className="text-[9px] md:text-xs font-black text-orange-500 uppercase block mb-2"
          >
            Communication Portal
          </motion.span>
          <h1 className="text-4xl md:text-8xl font-black text-white tracking-tighter italic uppercase leading-tight">
            GET IN <span className="text-orange-500">TOUCH</span>
          </h1>
        </motion.div>
      </section>

      {/* --- MAIN CONTENT AREA --- */}
      {/* Fixed: Reduced negative margin (-mt-4 / -mt-8) so containers stay below the title */}
      <section className="max-w-6xl mx-auto px-4 md:px-10 -mt-4 md:-mt-8 pb-16 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          
          {/* LEFT: INTERACTIVE INFO CARD */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-4 order-2 lg:order-1"
          >
            <div className="bg-slate-950 p-7 md:p-10 rounded-[2rem] text-white shadow-2xl h-full border border-white/5 flex flex-col justify-between overflow-hidden relative group">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-orange-500/10 blur-[50px] group-hover:bg-orange-500/20 transition-all duration-700" />
              
              <div>
                <h3 className="text-xl font-black italic uppercase mb-8 flex items-center gap-2">
                  <Zap size={20} className="text-orange-500 animate-pulse" /> Company
                </h3>
                
                <div className="space-y-6">
                  <ContactInfoItem delay={0.1} icon={<MapPin size={18}/>} title="Company" desc="Hosepate, Karnataka, India" />
                  <ContactInfoItem delay={0.2} icon={<Phone size={18}/>} title="Phone" desc="+91 94498 26754" />
                  <ContactInfoItem delay={0.3} icon={<Mail size={18}/>} title="Email" desc="marvelhpt02@gmail.com" />
                </div>
              </div>

              <div className="mt-12 pt-6 border-t border-white/5">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Availability</p>
                <p className="text-sm font-bold text-orange-500">Mon — Sat</p>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: ANIMATED FORM CARD */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-8 order-1 lg:order-2"
          >
            <div className="bg-white p-6 md:p-12 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100 h-full">
              <AnimatePresence mode="wait">
                {status.success ? (
                  <SuccessUI onReset={() => setStatus({ ...status, success: false })} />
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5 md:space-y-7">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
                      <CompactInput delay={0.1} label="Identity" name="customerName" type="text" placeholder="Full Name" value={formData.customerName} onChange={handleChange} />
                      <CompactInput delay={0.2} label="Email System" name="customerEmail" type="email" placeholder="Email Address" value={formData.customerEmail} onChange={handleChange} />
                    </div>
                    
                    <CompactInput delay={0.3} label="Direct Contact" name="customerPhone" type="tel" placeholder="+91 XXX XXX XXXX" value={formData.customerPhone} onChange={handleChange} />
                    
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-1"
                    >
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Specifications / Message</label>
                      <textarea 
                        required name="message" rows="4" value={formData.message} onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 px-4 py-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all font-semibold text-slate-800 text-sm md:text-base resize-none hover:border-orange-200"
                        placeholder="Describe your technical requirements..."
                      />
                    </motion.div>

                    {status.error && <p className="text-red-500 text-[10px] font-bold uppercase tracking-widest bg-red-50 p-2 rounded-lg text-center">{status.error}</p>}

                    <motion.button 
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={status.loading}
                      type="submit" 
                      className="w-full bg-slate-900 hover:bg-orange-500 text-white font-black py-4 md:py-5 rounded-2xl transition-all duration-500 shadow-xl flex items-center justify-center gap-3 uppercase italic text-sm md:text-base tracking-tighter"
                    >
                      {status.loading ? "Processing Transmission..." : "Send Message"}
                      <Send size={18} className="group-hover:translate-x-1" />
                    </motion.button>
                  </form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;